import {
  FormControl,
  FormHelperText,
  SxProps,
  Theme,
  InputLabel,
  MenuItem,
  Select,
  BaseTextFieldProps,
  Chip,
  Box,
} from '@mui/material'
import { PropsWithChildren } from 'react'
import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form'
import { mobileFieldGapSx } from './mobileFieldGapSx'
import { getSoftChipSx } from './chipStyles'

interface Option {
  label: string
  value: string | number
  color: string
}

type Props<T extends FieldValues> = {
  sx?: SxProps<Theme>
  control: Control<T>
  error: FieldError | undefined
  label?: string
  fieldName: Path<T>
  options: Array<Option>
  requiredRule: string | undefined
  size?: BaseTextFieldProps['size']
  disabled?: boolean
  readOnly?: boolean
  chipTone?: 'soft' | 'important'
}

export function ColoredChipSelectField<T extends FieldValues>({
  sx,
  control,
  options,
  error,
  fieldName,
  label,
  requiredRule,
  size,
  disabled,
  readOnly,
  chipTone = 'soft',
}: PropsWithChildren<Props<T>>) {
  const labelId = `${fieldName}_label`

  return (
    <FormControl
      fullWidth
      variant="outlined"
      sx={sx ? ([mobileFieldGapSx, sx] as SxProps<Theme>) : mobileFieldGapSx}
      required={Boolean(requiredRule)}
    >
      <InputLabel htmlFor={labelId} error={!!error} size={size === 'small' ? 'small' : 'normal'}>
        {label}
      </InputLabel>
      <Controller
        name={fieldName}
        control={control}
        rules={{ required: requiredRule }}
        defaultValue={'' as any} // TODO: check this
        render={({ field: { onChange, value } }) => (
          <Select
            value={value}
            onChange={readOnly ? undefined : onChange}
            label={label}
            labelId={labelId}
            error={!!error}
            required={Boolean(requiredRule)}
            size={size}
            disabled={disabled}
            open={readOnly ? false : undefined}
            onOpen={readOnly ? () => undefined : undefined}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                {options.map(
                  (option) =>
                    selected === option.value && (
                      <Chip
                        key={option.value}
                        label={option.label}
                        sx={getSoftChipSx(option.color, { emphasis: chipTone === 'important' })}
                      />
                    )
                )}
              </Box>
            )}
          >
            {options.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                <Chip label={option.label} sx={getSoftChipSx(option.color, { emphasis: chipTone === 'important' })} />
              </MenuItem>
            ))}
          </Select>
        )}
      />

      <FormHelperText error={!!error}>{error?.message}</FormHelperText>
    </FormControl>
  )
}
